import { DeviceDTO, DeviceService } from '@activia/cm-api';
import { IDataTableColumnConfig, IDataTableDataFetchEvent, IDataTableDataSource } from '@activia/ngx-components';
import { extractTotalRecordsFromHttpHeaders } from '@amp/utils/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { BehaviorSubject, concat, map, Observable, of, ReplaySubject, scan, share, Subject } from 'rxjs';
import { DEFAULT_DRAG_DROP_ROW_ID } from '../../models/player-unit.enum';
import { concatMap, skip, switchMap, takeUntil } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { PlayerColumnService } from '../../players/services/player-column.service';
import { UNLINKED_PLAYER_COLS } from '../../players/models/player-column';
import { DeviceState } from '../../store/device/device.reducer';
import { Store } from '@ngrx/store';
import { selectDeviceEntities } from '../../store/device/device.selectors';
import { Dictionary } from '@ngrx/entity';
import { SiteManagementService } from '../../services/site-management.service';

// Filter to get all the devices without assigned site
const DEFAULT_FILTER = `( site.id.defined="false" ) &  ( type.type = "Digital Signage Network Player" )`;

@Component({
  selector: 'amp-site-management-provision-list',
  templateUrl: './site-management-provision-list.component.html',
  styleUrls: ['./site-management-provision-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiteManagementProvisionListComponent implements OnInit, OnDestroy {
  /** The cell template for drag indicator */
  @ViewChild('dragIndicatorTemplate', { static: true }) dragIndicatorTemplate: TemplateRef<any>;

  /** List of all devices not binded with a site */
  devicesDataSource$: Observable<IDataTableDataSource<DeviceDTO>>;

  /** The recent devices data table columns configuration */
  columns$: Observable<IDataTableColumnConfig<void>[]>;

  /** Event everytime grid fetch devices */
  fetchDevices$ = new Subject<IDataTableDataFetchEvent>();

  editable$: Observable<boolean>;

  /** Filter devices by hostname, serial number or mac adress */
  filter$: BehaviorSubject<string> = new BehaviorSubject(DEFAULT_FILTER);

  /** @ignore Pattern used to close all subscriptions*/
  private componentDestroyed$: Subject<void> = new Subject<void>();

  constructor(
    private _deviceService: DeviceService,
    private _playerColumnService: PlayerColumnService,
    private _store: Store<DeviceState>,
    private _siteManagementService: SiteManagementService
  ) {
    this.editable$ = this._siteManagementService.hasAuthority$('site');
  }

  public ngOnInit(): void {
    this.setColumns();

    const devices$: Observable<{ devices: DeviceDTO[]; count: number }> = this.filter$.pipe(
      switchMap((filter: string) => concat(of({ devices: [], count: 1 }), this.getDevices$(filter))),
      skip(1), // Skip the first event {devices: [], count: 1} because the data-table will do it when initialized
      takeUntil(this.componentDestroyed$)
    );

    this.devicesDataSource$ = devices$.pipe(
      switchMap((devices) => this._store.select(selectDeviceEntities).pipe(map((linkedDevices) => [linkedDevices, devices] as [Dictionary<DeviceDTO>, { devices: DeviceDTO[]; count: number }]))),
      map(([linkedDevices, devices]) => {
        const rows = devices.devices.filter((e) => !linkedDevices[e.id]);

        return {
          rows,
          totalRowCount: devices.count,
        };
      }),
      share({
        connector: () => new ReplaySubject(1),
        resetOnRefCountZero: true,
        resetOnComplete: false,
        resetOnError: false,
      })
    );
  }

  /** @ignore */
  public ngOnDestroy(): void {
    // unsubscribe all subscriptions
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }

  setColumns(): void {
    this.columns$ = this._playerColumnService.getFormattedPlayerColumns$(UNLINKED_PLAYER_COLS).pipe(
      map((columns) => [
        {
          id: 'dragIndicator',
          name: null,
          prop: 'dragIndicator',
          dataCellTemplate: this.dragIndicatorTemplate,
          sortable: false,
          resizable: false,
          draggable: false,
          widthPx: 20,
        },
        ...columns,
      ])
    );
  }

  /** Search the specific device by hostname, serial number or mac address */
  public searchDevices(value: string) {
    const searchExpression = value.trim();
    if (searchExpression.length > 2 || searchExpression.length === 0) {
      const filter =
        searchExpression.length === 0
          ? DEFAULT_FILTER
          : `( serial ~ "${searchExpression}" | interface.hostname ~ "${searchExpression}" | interface.ip ~ "${searchExpression}" | interface.mac ~ "${searchExpression}" ) & ${DEFAULT_FILTER}`;
      this.filter$.next(filter);
    }
  }

  public fetchDevices(fetchEvent: IDataTableDataFetchEvent): void {
    this.fetchDevices$.next(fetchEvent);
  }

  private mapDevices(response: HttpResponse<Array<DeviceDTO>>): { devices: DeviceDTO[]; count: number } {
    const totalDevices = extractTotalRecordsFromHttpHeaders<DeviceDTO>(response);
    const devices = response.body.map((device) => ({
      ...device,
      datatableRowOptions: {
        dragOriginId: DEFAULT_DRAG_DROP_ROW_ID,
      },
    }));

    return { count: totalDevices, devices };
  }

  private getDevices$(filter: string): Observable<{ devices: DeviceDTO[]; count: number }> {
    // Lazy loading devices with virtual scroll
    return this.fetchDevices$.pipe(
      concatMap((fetchEvent: IDataTableDataFetchEvent) => this._deviceService.getDevices(fetchEvent.amount, fetchEvent.offset, null, null, filter, null, null, ['*'], null, 'response')),
      map((response: HttpResponse<Array<DeviceDTO>>) => this.mapDevices(response)),
      scan(
        (all, { devices, count }) => ({
          devices: [...all.devices, ...devices],
          count,
        }),
        { devices: [], count: 0 }
      )
    );
  }
}
